const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export async function createTicket(): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/platform-api/ticket/create`, {
    method: 'POST',
    credentials: "include",
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      channel: 'web',
      productCode: 'deepsearch-s1-platform',
      url: '/session/history',
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to create ticket');
  }

  const data = await response.json();
  return data.data.ticket;
}

export async function createSession(question: string, ticket: string): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/deepsearch-api/session`, {
    method: 'POST',
    credentials: "include",
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      question,
      ticket,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to create session');
  }

  const data = await response.json();
  return data.data.sessionId;
}

export function createEventSource(
  sessionId: string,
  ticket: string,
  onMessage: (message: any) => void,
  onError: (error: Error) => void,
  onComplete: () => void
): EventSource {
  const url = `${API_BASE_URL}/deepsearch-api/session/${sessionId}/read`;
  
  // 使用 fetch + ReadableStream 来处理 POST 请求的 SSE
  const abortController = new AbortController();
  
  fetch(url, {
    method: 'POST',
    credentials: "include",
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'text/event-stream',
    },
    body: JSON.stringify({
      ticket,
      history: false,
    }),
    signal: abortController.signal,
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No reader available');
      }
      
      const decoder = new TextDecoder();
      let buffer = '';
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete();
          break;
        }
        
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data.trim()) {
              try {
                const parsed = JSON.parse(data);
                onMessage(parsed);
              } catch (e) {
                console.error('Failed to parse SSE data:', e);
              }
            }
          }
        }
      }
    })
    .catch((error) => {
      if (error.name !== 'AbortError') {
        onError(error);
      }
    });
  
  // 返回一个模拟的 EventSource 对象
  return {
    close: () => abortController.abort(),
  } as EventSource;
}

export interface ExportPDFPayload {
  title: string;
  sections: Array<{
    type: 'markdown' | 'chart_image';
    content: string;
    title?: string;
  }>;
}

export async function exportPDF(sessionId: string, payload: ExportPDFPayload): Promise<Blob> {
  const response = await fetch(`${API_BASE_URL}/deepsearch-api/session/${sessionId}/export-pdf`, {
    method: 'POST',
    credentials: "include",
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.detail || 'Failed to export PDF');
  }

  return response.blob();
}
